import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import * as Sentry from '@sentry/react'
import { Banner, Link, useCookie, WarningIcon } from '@buffer-mono/popcorn'
import { useSplitEnabled } from '@buffer-mono/features'
import { useAccount } from '../../../../hooks'
import { fetchStatusIncidents } from '@buffer-mono/shared-utils/status'
import type { Incident } from '@buffer-mono/shared-utils/status'
import { filterIncidentsForArea } from '../../utils/filterIncidentsForArea'
import { filterIncidentsForOrg } from '../../utils/filterIncidentsForOrg'
import { getActiveAppArea } from '../../utils/getActiveAppArea'
import type { AppArea } from '../../utils/getActiveAppArea'
import { useHostNavigation } from '../../../../common/hostNavigation/HostNavigationContext'

const STATUS_PAGE_URL = 'https://status.buffer.com/'

// Report each unmapped component name once per session and report type (the
// same name can be unrecognized both as a network and as a surface).
// captureMessage sends a standalone Sentry event (a breadcrumb alone never
// surfaces without an error), and the module-level set stops re-renders from
// re-reporting the same name.
const reportedComponents = new Set<string>()

const reportUnrecognizedComponents = (
  componentNames: string[],
  message: string,
): void => {
  const fresh = componentNames.filter(
    (name) => !reportedComponents.has(`${message}:${name}`),
  )
  if (fresh.length === 0) {
    return
  }
  fresh.forEach((name) => reportedComponents.add(`${message}:${name}`))
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: { component: 'AutomaticStatusBanner' },
    extra: { componentNames: fresh },
  })
}

const isLoginOnlyIncident = (incident: Incident): boolean => {
  const components = incident.affected_components ?? []
  return (
    components.length > 0 &&
    components.every(
      (component) =>
        typeof component.name === 'string' &&
        component.name.trim().toLowerCase() === 'login',
    )
  )
}

const AutomaticStatusBanner = (): JSX.Element | null => {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [dismissedIncidents, setDismissedIncidents] = useCookie<string[]>(
    'buffer-status-dismissed-incidents',
    [],
  )

  const { account, loading: accountLoading } = useAccount()
  const { isEnabled: isChannelScopingEnabled, isReady: isChannelSplitReady } =
    useSplitEnabled('incident-banner-channel-scoping')
  const { isEnabled: isAreaScopingEnabled, isReady: isAreaSplitReady } =
    useSplitEnabled('incident-banner-page-scoping')

  // The area is read from the URL, but moving between areas that share a
  // subdomain (see getActiveAppArea) is client-side navigation that never
  // remounts the app shell — so recompute it whenever the host router's
  // pathname changes. Without a host (satellites) the area is fixed at mount.
  const hostPathname = useHostNavigation()?.pathname
  const activeArea = useMemo<AppArea | null>(
    () => getActiveAppArea(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read window.location on host navigation
    [hostPathname],
  )

  const {
    relevant,
    unrecognizedSocialComponents,
    unrecognizedSurfaceComponents,
  } = useMemo(() => {
    const appShellIncidents = incidents.filter(
      (incident) => !isLoginOnlyIncident(incident),
    )

    // Gated rollout: until the split is ready and on, keep the pre-scoping
    // behavior (show every incident). Same fallback if the account query
    // returned no organization — undefined, or a partial response with
    // `currentOrganization: null` under errorPolicy 'all' — so we fail open
    // rather than silently hide real incidents. A real org always has a
    // `currentOrganization` (zero channels still gives `channels: []`).
    const channelFiltered =
      !isChannelSplitReady ||
      !isChannelScopingEnabled ||
      !account?.currentOrganization
        ? { relevant: appShellIncidents, unrecognizedComponents: [] }
        : filterIncidentsForOrg(
            appShellIncidents,
            new Set(
              (account.currentOrganization.channels ?? []).map(
                (channel) => channel.service,
              ),
            ),
          )

    // Independently gated second pass: scope what survived channel scoping
    // to the app area being viewed.
    const areaFiltered =
      !isAreaSplitReady || !isAreaScopingEnabled
        ? { relevant: channelFiltered.relevant, unrecognizedComponents: [] }
        : filterIncidentsForArea(channelFiltered.relevant, activeArea)

    return {
      relevant: areaFiltered.relevant,
      unrecognizedSocialComponents: channelFiltered.unrecognizedComponents,
      unrecognizedSurfaceComponents: areaFiltered.unrecognizedComponents,
    }
  }, [
    incidents,
    account,
    isChannelScopingEnabled,
    isChannelSplitReady,
    isAreaScopingEnabled,
    isAreaSplitReady,
    activeArea,
  ])

  useEffect(() => {
    if (unrecognizedSocialComponents.length > 0) {
      reportUnrecognizedComponents(
        unrecognizedSocialComponents,
        'Unrecognized Social Networks status page component',
      )
    }
  }, [unrecognizedSocialComponents])

  useEffect(() => {
    if (unrecognizedSurfaceComponents.length > 0) {
      reportUnrecognizedComponents(
        unrecognizedSurfaceComponents,
        'Unrecognized status page component for area scoping',
      )
    }
  }, [unrecognizedSurfaceComponents])

  useEffect(() => {
    let cancelled = false

    fetchStatusIncidents()
      .then((fetchedIncidents) => {
        if (cancelled) {
          return null
        }
        setIncidents(fetchedIncidents)
        setLoading(false)
        setError(false)
        return null
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setError(true)
        setLoading(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [activeArea])

  // Wait for the org's channels before filtering, otherwise the banner would
  // briefly show unfiltered incidents while the account query is in flight.
  if (loading || (error && incidents.length === 0) || accountLoading) {
    return null
  }

  const incident = relevant.find(
    (candidate) => !dismissedIncidents.includes(candidate.id),
  )

  if (!incident) {
    return null
  }

  const handleDismiss = (): void => {
    setDismissedIncidents((prev) => [...prev, incident.id])
  }

  return (
    <Banner variant="warning" onDismiss={handleDismiss}>
      <Banner.Text icon={<WarningIcon />}>
        <strong>Buffer Status:</strong> {incident.name} —{' '}
        <Link
          color="inherit"
          href={STATUS_PAGE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline' }}
        >
          More details
        </Link>
      </Banner.Text>
    </Banner>
  )
}

export default AutomaticStatusBanner
